import React from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { selectedJobDataAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { isBlankRow } from './sectionDefs';
import AutoGrowTextarea from './AutoGrowTextarea';
import DayRecap from './DayRecap';
import PhotosStep from './PhotosStep';
import SetupStep from './SetupStep';
import WeatherBlock from './WeatherBlock';

// Filing block for the Quick Report (/daily-report2). It has no tab of its
// own — it sits at the bottom of the Equipment tab, under the equipment matrix
// and the subs, so the two-tab report still ends in a submit. Contents: the
// conditions block that used to be a Setup tab (weather, ground conditions,
// superintendent, the one-question safety block, issues/delays), then the
// productivity recap (today's rate against plan and last week, gain/fade in
// hours), then photos, notes and submit. The recap is read-only — every number
// on it comes from what the Progress tab already carries, so there is nothing
// extra to save.

const inputBase = {
    minHeight: 44,
    fontSize: 16,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 12,
    paddingRight: 12,
    backgroundColor: '#fff',
    boxSizing: 'border-box',
};

const card = {
    borderRadius: 12,
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    padding: 14,
    marginBottom: 12,
};

const SimpleReview = ({ readOnly, onSaveDraft, onSubmit, saving }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [jobData] = useAtom(selectedJobDataAtom);

    if (!report) return null;

    const setNotes = (value) => {
        setReport({ ...report, notes: value });
        setDirty(true);
    };

    const workRows = (report.sections?.work || []).filter((r) => !isBlankRow(r));
    const roStyle = readOnly ? { backgroundColor: '#F1F5F9' } : null;
    const jobName = jobData?.BU_Description || jobData?.Job_Description || '';
    // Client comes off the job record (Client_Name, aliased AR_AB_Name on the
    // job-details query).
    const client = jobData?.AR_AB_Name || jobData?.Client_Name || '';

    return (
        <View style={{ paddingBottom: 8 }}>
            {/* Report identity on the left, site weather (AM / PM / wind, one
                row each, GPS refresh) on the right. Stacks on narrow phones. */}
            <View style={card}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 16, rowGap: 12 }}>
                    <View style={{ flex: 1, minWidth: 190 }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>
                            {`${report.buId}${jobName ? ` — ${jobName}` : ''}`}
                        </Text>
                        {client ? (
                            <Text style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
                                <span style={{ fontWeight: 600, color: '#64748B' }}>Client · </span>{client}
                            </Text>
                        ) : null}
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                            {[
                                report.reportDate,
                                report.reportNumber ? `#${report.reportNumber}` : null,
                                report.status,
                            ].filter(Boolean).join(' · ')}
                        </Text>
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
                            {workRows.length
                                ? `${workRows.length} work ${workRows.length === 1 ? 'entry' : 'entries'} on the Progress tab.`
                                : 'No work entries yet — use the Progress tab to add what was done today.'}
                        </Text>
                    </View>
                    <WeatherBlock readOnly={readOnly} bare />
                </View>
            </View>

            {/* Conditions / safety / issues — the old Setup tab, embedded here
                so the Quick Report opens straight on the work. */}
            <View style={card}>
                <SetupStep readOnly={readOnly} simple embedded />
            </View>

            {/* The day played back — totals, per-phase pace and the things
                worth a second look before this gets submitted. */}
            <View style={card}>
                <DayRecap />
            </View>

            {/* Photos */}
            <View style={card}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A', marginBottom: 10 }}>Photos</Text>
                <PhotosStep readOnly={readOnly} />
            </View>

            {/* Notes */}
            <View style={card}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A', marginBottom: 10 }}>Notes</Text>
                <AutoGrowTextarea
                    value={report.notes || ''}
                    disabled={readOnly}
                    placeholder="Anything else worth a record — delays, visitors, safety, weather…"
                    onChange={(e) => setNotes(e.target.value)}
                    minHeight={88}
                    style={{ ...inputBase, width: '100%', fontFamily: 'inherit', ...roStyle }}
                />
            </View>

            {/* Actions */}
            {!readOnly && (
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', columnGap: 10, marginTop: 4 }}>
                    <Pressable
                        onPress={onSaveDraft}
                        disabled={saving}
                        style={{ minHeight: 48, paddingLeft: 18, paddingRight: 18, borderRadius: 10, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#1D4ED8' }}>{saving ? 'Saving…' : 'Save Draft'}</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => {
                            if (saving) return;
                            if (window.confirm('Submit this report? It becomes read-only after submitting.')) onSubmit();
                        }}
                        disabled={saving}
                        style={{ minHeight: 48, paddingLeft: 22, paddingRight: 22, borderRadius: 10, backgroundColor: '#16A34A', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>Submit Report</Text>
                    </Pressable>
                </View>
            )}
            {readOnly && (
                <View style={{ marginTop: 4, alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#065F46' }}>Submitted — read-only</Text>
                </View>
            )}
        </View>
    );
};

export default SimpleReview;
